import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { forwardRef, useImperativeHandle, useRef } from "react";

const PopupModal = forwardRef(
  (
    {
      open,
      handleClose,
      children,
      title,
      fullWidth = true,
      maxWidth = "md",
      height = "auto",
      actions,
      customClass,
      scrollOutside = true,
      size = "lg",
      width = null
    },
    ref
  ) => {
    const dialogContentRef = useRef(null);
    const theme = useTheme();
    // For very large screens, make popup narrower (responsive)
    const isXL = useMediaQuery(theme.breakpoints.up("xl"));
    //const isLG = useMediaQuery(theme.breakpoints.up("lg"));

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (dialogContentRef.current) {
          dialogContentRef.current.scrollTop = 0;
        }
      },
    }));

    const dialogSizes = {
      sm: { width: "40%", maxWidth: "50%", padding: "1rem" },
      md: { width: "70%", maxWidth: "80%", padding: "1.5rem" },
      lg: { width: "77%", maxWidth: "85%", padding: "2rem" },
      xl: { width: "90%", maxWidth: "95%", padding: "2rem" },
    };

    let currentSize = dialogSizes[size] || dialogSizes.md;

    // 👉 Custom numeric (60) or percentage ("60%") support
    if (!dialogSizes[size]) {
      if (!isNaN(size)) {
        currentSize = {
          width: `${size}%`,
          maxWidth: `${size}%`,
          padding: "1.5rem",
        };
      } else if (typeof size === "string" && size.endsWith("%")) {
        currentSize = {
          width: size,
          maxWidth: size,
          padding: "1.5rem",
        };
      }
    }
    // Explicit override wins
    if (width) {
      currentSize = {
        ...currentSize,
        width,
        maxWidth: width,
      };
    }


    return (
      <Dialog
        className={customClass}
        open={open}
        onClose={handleClose}
        fullWidth={fullWidth}
        maxWidth={false}
        // Scroll behavior based on the scrollOutside prop
        scroll={scrollOutside ? "body" : "paper"}
        disableScrollLock={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            position: "relative",
            overflowY: scrollOutside ? "visible" : "auto",
            ...currentSize, // 👈 applies small/medium/large sizing
          },
        }}
      >
        <DialogTitle sx={{ position: "relative", padding: "1rem 1.5rem" }}>
          {title}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: "1rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "black",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          ref={dialogContentRef}
          sx={{
            padding: "1.5rem",
            // Different overflow based on scroll behavior
            overflow: scrollOutside ? "visible" : "auto",
            // Set height only for inside scroll
            ...(!scrollOutside && {
              minHeight: `calc(${height} - 120px)`,
              maxHeight: "60vh",
            }),
          }}
        >
          {children}
        </DialogContent>

        {actions && (
          <>
            <Divider />
            <DialogActions sx={{ padding: "1rem 1.5rem" }}>{actions}</DialogActions>
          </>
        )}
      </Dialog>
    );
  }
);

export default PopupModal;
